import React, { useEffect, useRef } from 'react';
import { Avatar, Button, EmptyRow, MoreMenu, SkeletonRows, StatusPill, relativeTime } from '../kit';
import {
  ROLE_LABELS,
  STATUS_PILL,
  displayName,
  isDeactivated,
  locationAccessLabel,
  orgRoleChips,
  reportCodes,
  shortDate,
  userStatus,
} from './userModel';

const COLUMNS = 8;

function SelectAll({ rows, selected, onToggleAll }) {
  const ref = useRef(null);
  const n = rows.filter((r) => selected.has(r.id)).length;
  const all = rows.length > 0 && n === rows.length;
  useEffect(() => { if (ref.current) ref.current.indeterminate = n > 0 && !all; }, [n, all]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className="ak-check"
      aria-label="Select all users on this page"
      checked={all}
      disabled={!rows.length}
      onChange={() => onToggleAll(!all)}
    />
  );
}

function OrgCell({ user, orgNameByCode }) {
  const chips = orgRoleChips(user, orgNameByCode);
  if (!chips.length) return <span className="ak-muted">No organizations</span>;
  const shown = chips.slice(0, 1);
  const more = chips.length - shown.length;
  return (
    <div className="us-org-cell" title={chips.map((c) => `${c.name} · ${ROLE_LABELS[c.role] || c.role || 'No role'}`).join('\n')}>
      {shown.map((c) => (
        <span key={c.code} className="ak-role">{c.name} <span>· {ROLE_LABELS[c.role] || c.role || 'No role'}</span></span>
      ))}
      {more > 0 ? <span className="ak-muted us-more">+{more} org{more === 1 ? '' : 's'}</span> : null}
    </div>
  );
}

function LastSignIn({ user, status }) {
  if (user.lastSignInAt) return <span title={new Date(user.lastSignInAt).toLocaleString()}>{relativeTime(user.lastSignInAt)}</span>;
  if (status === 'invited') return <span>Invited {shortDate(user.lastInvitedAt || user.invitedAt)}</span>;
  return <span className="ak-muted" title="No sign-in recorded yet">—</span>;
}

/**
 * rowActions(user) → MoreMenu items. onOpen(user) opens the panel.
 */
export default function UsersTable({
  rows,
  loading,
  loaded,
  error,
  onRetry,
  selected,
  onToggle,
  onToggleAll,
  onOpen,
  rowActions,
  orgNameByCode,
  emptyState,
}) {
  return (
    <div className="ak-scroll">
      <table className={`ak-tbl us-tbl${loading && loaded ? ' us-busy' : ''}`} aria-busy={loading ? 'true' : 'false'}>
        <thead>
          <tr>
            <th className="ak-w-check"><SelectAll rows={rows} selected={selected} onToggleAll={onToggleAll} /></th>
            <th>User</th>
            <th>Organizations · role</th>
            <th className="ak-hide-sm">Location access</th>
            <th className="ak-hide-sm">Reports</th>
            <th>Status</th>
            <th className="ak-hide-sm">Last sign-in</th>
            <th className="ak-w-act"><span className="ak-sr">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {!loaded && loading ? <SkeletonRows rows={8} columns={COLUMNS} /> : null}
          {error && !loading ? (
            <tr>
              <td colSpan={COLUMNS}>
                <div className="ak-error" role="alert">
                  <span style={{ flex: 1 }}>Couldn't load users. {error}</span>
                  <Button size="small" onClick={onRetry}>Retry</Button>
                </div>
              </td>
            </tr>
          ) : null}
          {loaded && !error && !rows.length && !loading ? (
            <EmptyRow colSpan={COLUMNS} title={emptyState.title} body={emptyState.body} action={emptyState.action} />
          ) : null}
          {rows.map((u) => {
            const status = u.adminMeta?.status || userStatus(u);
            const pill = STATUS_PILL[status] || STATUS_PILL.active;
            const isSel = selected.has(u.id);
            const cls = ['ak-click'];
            if (isSel) cls.push('ak-selected');
            if (isDeactivated(u)) cls.push('ak-faded');
            return (
              <tr key={u.id} className={cls.join(' ')} onClick={() => onOpen(u)}>
                <td className="ak-w-check" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="ak-check"
                    aria-label={`Select ${displayName(u)}`}
                    checked={isSel}
                    onChange={() => onToggle(u)}
                  />
                </td>
                <td>
                  <div className="ak-cell-id">
                    <Avatar name={displayName(u)} email={u.email} />
                    <div>
                      <button
                        type="button"
                        className="us-rowlink ak-cell-title"
                        onClick={(e) => { e.stopPropagation(); onOpen(u); }}
                      >
                        {displayName(u)}
                      </button>
                      <span className="ak-cell-sub">{u.email}</span>
                    </div>
                  </div>
                </td>
                <td><OrgCell user={u} orgNameByCode={orgNameByCode} /></td>
                <td className="ak-hide-sm">{locationAccessLabel(u)}</td>
                <td className="ak-hide-sm"><span className="ak-mono us-codes">{reportCodes(u) || '—'}</span></td>
                <td><StatusPill tone={pill.tone}>{pill.label}</StatusPill></td>
                <td className="ak-hide-sm"><LastSignIn user={u} status={status} /></td>
                <td className="ak-w-act" onClick={(e) => e.stopPropagation()}>
                  <MoreMenu items={rowActions(u)} label={`Actions for ${displayName(u)}`} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
